// frontend/src/pages/crm/Calendar.js
import React, { useState, useEffect } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, subDays, isWithinInterval, isSameDay, getISOWeek } from 'date-fns';
import { calendarAPI, clientAPI, usersAPI } from '../../utils/crmApi';
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusIcon,
    ClockIcon,
    MapPinIcon,
    UserGroupIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import {
    CalendarProvider,
    CalendarDate,
    CalendarDatePicker,
    CalendarMonthPicker,
    CalendarYearPicker,
    CalendarDatePagination,
    CalendarHeader,
    CalendarBody,
    CalendarItem
} from '../../components/kibo-ui/calendar';
import toast from 'react-hot-toast';
import { useDialog } from '../../components/kibo-ui/dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
// shadcn/ui
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const Calendar = () => {
    const { branding } = useBranding();
    const { openDialog } = useDialog();
    const [events, setEvents] = useState([]);
    const [clients, setClients] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('month'); // month, week, day
    const [filterRange, setFilterRange] = useState({ from: null, to: null });
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        startAt: '',
        endAt: '',
        startAtEU: '',
        endAtEU: '',
        type: 'meeting',
        clientId: '',
        attendees: [],
        location: '',
        isVirtual: false,
        meetingLink: '',
        color: branding?.primaryColor || '#3B82F6'
    });
    const [formErrors, setFormErrors] = useState({ startAtEU: '', endAtEU: '' });

    const formatEUInput = (date) => date ? format(new Date(date), 'dd-MM-yyyy HH:mm') : '';
    const parseEUInput = (value) => {
        // Expecting dd-MM-yyyy HH:mm
        try {
            const [d, t] = value.split(' ');
            if (!d || !t) return null;
            const [day, month, year] = d.split('-').map(Number);
            const [hour, minute] = t.split(':').map(Number);
            if (!day || !month || !year || hour===undefined || minute===undefined) return null;
            const dt = new Date(year, month - 1, day, hour, minute, 0);
            if (isNaN(dt.getTime())) return null;
            return dt;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        loadData();
        // Load agency users for attendees once
        (async () => {
            try {
                const res = await usersAPI.getAgents();
                if (res.data?.success) setAgents(res.data.data || []);
            } catch (e) {}
        })();
    }, [currentDate, filterRange.from, filterRange.to, viewMode]);

    const loadData = async () => {
        try {
            setLoading(true);
            let start;
            let end;
            if (filterRange.from && filterRange.to) {
                start = filterRange.from;
                end = filterRange.to;
            } else {
                if (viewMode === 'week') {
                    start = startOfWeek(currentDate, { weekStartsOn: 1 });
                    end = endOfWeek(currentDate, { weekStartsOn: 1 });
                } else if (viewMode === 'day') {
                    start = startOfDay(currentDate);
                    end = endOfDay(currentDate);
                } else {
                    start = startOfMonth(currentDate);
                    end = endOfMonth(currentDate);
                }
            }
            
            const apiStart = startOfDay(start);
            // add 1 day to make backend's $lte inclusive of end day at 00:00
            const apiEndNext = addDays(endOfDay(end), 1);
            
            const [eventsRes, clientsRes] = await Promise.all([
                calendarAPI.getEvents({
                    startDate: format(apiStart, 'yyyy-MM-dd'),
                    endDate: format(apiEndNext, 'yyyy-MM-dd')
                }),
                clientAPI.getAll({ limit: 100 })
            ]);

            if (eventsRes.data.success) {
                setEvents(eventsRes.data.data);
            }

            if (clientsRes.data.success) {
                setClients(clientsRes.data.data);
            }
        } catch (error) {
            console.error('Failed to load calendar data:', error);
            toast.error('Failed to load calendar');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviousMonth = () => {
        if (viewMode === 'week') {
            setCurrentDate(subDays(currentDate, 7));
        } else if (viewMode === 'day') {
            setCurrentDate(subDays(currentDate, 1));
        } else {
        setCurrentDate(subMonths(currentDate, 1));
        }
    };

    const handleNextMonth = () => {
        if (viewMode === 'week') {
            setCurrentDate(addDays(currentDate, 7));
        } else if (viewMode === 'day') {
            setCurrentDate(addDays(currentDate, 1));
        } else {
        setCurrentDate(addMonths(currentDate, 1));
        }
    };

    const handleToday = () => {
        const today = startOfDay(new Date());
        setCurrentDate(today);
        setFilterRange({ from: null, to: null });
        // Ensure day view snaps correctly when user expects it
        if (viewMode === 'day') {
            // trigger re-render by temporarily toggling
            setViewMode('week');
            setTimeout(() => setViewMode('day'), 0);
        }
    };

    const changeViewMode = (mode) => {
        if (!mode) return;
        setViewMode(mode);
        if (mode === 'month') {
            // Ensure month view reflects the selected month fully
            setFilterRange({ from: null, to: null });
        }
    };

    const handleCreateEvent = () => {
        setSelectedEvent(null);
        setFormData({
            title: '',
            description: '',
            startAt: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
            endAt: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
            startAtEU: format(new Date(), 'dd-MM-yyyy HH:mm'),
            endAtEU: format(new Date(), 'dd-MM-yyyy HH:mm'),
            type: 'meeting',
            clientId: '',
            attendees: [],
            location: '',
            isVirtual: false,
            meetingLink: '',
            color: '#3B82F6'
        });
        setFormErrors({ startAtEU: '', endAtEU: '' });
        setShowEventModal(true);
    };

    const openEventDetails = (event) => {
        setSelectedEvent(event);
        setShowEventDetails(true);
    };

    const handleEditEvent = (event) => {
        setSelectedEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            startAt: format(new Date(event.startAt), 'yyyy-MM-dd\'T\'HH:mm'),
            endAt: format(new Date(event.endAt), 'yyyy-MM-dd\'T\'HH:mm'),
            startAtEU: format(new Date(event.startAt), 'dd-MM-yyyy HH:mm'),
            endAtEU: format(new Date(event.endAt), 'dd-MM-yyyy HH:mm'),
            type: event.type,
            clientId: event.clientId?._id || '',
            attendees: (event.attendees || []).map(a => a._id || a),
            location: event.location || '',
            isVirtual: event.isVirtual,
            meetingLink: event.meetingLink || '',
            color: event.color
        });
        setFormErrors({ startAtEU: '', endAtEU: '' });
        setShowEventModal(true);
        setShowEventDetails(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Event title is required');
            return;
        }

        try {
            // Validate dates
            const sdt = parseEUInput(formData.startAtEU);
            const edt = parseEUInput(formData.endAtEU);
            const newErrors = { startAtEU: '', endAtEU: '' };
            if (!sdt) newErrors.startAtEU = 'Invalid date/time';
            if (!edt) newErrors.endAtEU = 'Invalid date/time';
            if (sdt && edt && edt < sdt) newErrors.endAtEU = 'End must be after start';
            setFormErrors(newErrors);
            if (newErrors.startAtEU || newErrors.endAtEU) return;

            if (selectedEvent) {
                await calendarAPI.update(selectedEvent._id, formData);
                toast.success('Event updated successfully!');
            } else {
                await calendarAPI.create(formData);
                toast.success('Event created successfully!');
            }
            
            setShowEventModal(false);
            loadData();
        } catch (error) {
            console.error('Failed to save event:', error);
            toast.error('Failed to save event');
        }
    };

    const handleDeleteEvent = () => {
        openDialog({
            title: 'Delete Event',
            description: 'Are you sure you want to delete this event? This action cannot be undone.',
            confirmText: 'Delete Event',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await calendarAPI.delete(selectedEvent._id);
                    toast.success('Event deleted successfully!');
                    setShowEventModal(false);
                    loadData();
                } catch (error) {
                    toast.error('Failed to delete event');
                }
            }
        });
    };

    const getEventTypeColor = (type) => {
        const colors = {
            meeting: '#3B82F6',
            deadline: '#EF4444',
            appointment: '#10B981',
            task_due: '#F59E0B',
            reminder: '#8B5CF6',
            other: '#6B7280'
        };
        return colors[type] || colors.other;
    };

    // Normalize event dates (defensive against API shape/timezones)
    const normEvents = events.map(e => {
        const startRaw = e.startAt || e.start || e.start_date;
        const endRaw = e.endAt || e.end || e.end_date;
        const startAt = startRaw ? new Date(startRaw) : null;
        const endAt = endRaw ? new Date(endRaw) : null;
        return {
            ...e,
            startAt,
            endAt
        };
    }).filter(e => e.startAt instanceof Date && !isNaN(e.startAt) && e.endAt instanceof Date && !isNaN(e.endAt));

    // DEBUG: normalized events
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Calendar] viewMode:', viewMode, 'currentDate:', currentDate);
            console.log('[Calendar] filterRange:', filterRange);
            console.log('[Calendar] normEvents:', normEvents.map(ev => ({ id: ev._id, title: ev.title, startAt: ev.startAt?.toISOString?.(), endAt: ev.endAt?.toISOString?.() })));
        }
    } catch (_) {}

    // Transform for month Kibo body (still used in month view previously)
    const calendarEvents = normEvents.map(event => ({
        name: event.title,
        endAt: event.endAt,
        status: { color: event.color || getEventTypeColor(event.type) },
        _id: event._id,
        fullEvent: event
    }));

    // Visible range helpers for week/day custom views
    const getVisibleRange = () => {
        if (filterRange.from && filterRange.to) {
            return { start: filterRange.from, end: filterRange.to };
        }
        if (viewMode === 'week') {
            return {
                start: startOfWeek(currentDate, { weekStartsOn: 1 }),
                end: endOfWeek(currentDate, { weekStartsOn: 1 })
            };
        }
        if (viewMode === 'day') {
            return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
        }
        return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    };

    const { start: visibleStart, end: visibleEnd } = getVisibleRange();

    // DEBUG: visible range
    try {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[Calendar] visible range:', { start: visibleStart?.toISOString?.(), end: visibleEnd?.toISOString?.() });
        }
    } catch (_) {}

    const isWithin = (d) => d >= visibleStart && d <= visibleEnd;
    const eventsInRange = normEvents.filter(e => {
        const s = e.startAt;
        const en = e.endAt;
        return isWithin(s) || isWithin(en) || (s <= visibleStart && en >= visibleEnd);
    });

    const getWeekDays = (date) => {
        const start = startOfWeek(date, { weekStartsOn: 1 });
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    };

    const formatEU = (d, fmt) => format(d, fmt);

    // Build responsive header labels
    const labelLong = viewMode === 'week'
        ? `${formatEU(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd-MM-yyyy')} - ${formatEU(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd-MM-yyyy')}`
        : viewMode === 'day'
            ? `${formatEU(currentDate, 'dd-MM-yyyy')}`
            : `${formatEU(currentDate, 'MMMM yyyy')}`;

    const labelShort = viewMode === 'week'
        ? `${formatEU(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd-MM')}–${formatEU(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd-MM')}`
        : viewMode === 'day'
            ? `${formatEU(currentDate, 'dd-MM')}`
            : `${formatEU(currentDate, 'MMM yyyy')}`;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div 
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderBottomColor: branding?.primaryColor || '#3b82f6' }}
                ></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header (shadcn toolbar) */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={handleToday}>Today</Button>
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" onClick={handlePreviousMonth} aria-label="Previous">
                            <ChevronLeftIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={handleNextMonth} aria-label="Next">
                            <ChevronRightIcon className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="ml-2 text-foreground font-medium flex items-center gap-2 min-w-0">
                        {viewMode === 'week' && (
                            <span className="inline-flex items-center text-xs sm:text-sm px-2 py-1 rounded bg-muted">
                                W{getISOWeek(currentDate)}
                            </span>
                        )}
                        <span className="hidden sm:inline text-sm truncate max-w-[260px]">{labelLong}</span>
                        <span className="inline sm:hidden text-xs px-2 py-1 rounded bg-muted">{labelShort}</span>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="justify-start w-[220px]">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {filterRange.from && filterRange.to
                                    ? `${format(filterRange.from, 'dd-MM-yyyy')} - ${format(filterRange.to, 'dd-MM-yyyy')}`
                                    : 'Date range'}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-4" align="end">
                            <div className="space-y-3">
                                <div className="text-sm font-medium text-foreground">Filter by range</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-muted-foreground mb-1">From</label>
                                        <input
                                            type="date"
                                            value={filterRange.from ? format(filterRange.from, 'yyyy-MM-dd') : ''}
                                            onChange={(e) => setFilterRange({ ...filterRange, from: e.target.value ? new Date(e.target.value) : null })}
                                            className="w-full px-2 py-1 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded bg-background text-foreground"
                                        />
                                    </div>
                <div>
                                        <label className="block text-xs text-muted-foreground mb-1">To</label>
                                        <input
                                            type="date"
                                            value={filterRange.to ? format(filterRange.to, 'yyyy-MM-dd') : ''}
                                            onChange={(e) => setFilterRange({ ...filterRange, to: e.target.value ? new Date(e.target.value) : null })}
                                            className="w-full px-2 py-1 border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded bg-background text-foreground"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button variant="outline" size="sm" onClick={() => setFilterRange({ from: null, to: null })}>Clear</Button>
                                    <Button 
                                        size="sm" 
                                        onClick={() => { /* popover auto closes on blur */ }}
                                        className="text-white"
                                        style={{ backgroundColor: branding?.primaryColor || '#3b82f6' }}
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>

                    <div className="ml-1 hidden sm:flex items-center gap-1">
                        <Button 
                            variant={viewMode==='month' ? 'default' : 'outline'} 
                            size="sm" 
                            onClick={() => changeViewMode('month')}
                            className={viewMode==='month' ? 'text-white' : ''}
                            style={viewMode==='month' ? { backgroundColor: branding?.primaryColor || '#3b82f6' } : {}}
                        >
                            Month
                        </Button>
                        <Button 
                            variant={viewMode==='week' ? 'default' : 'outline'} 
                            size="sm" 
                            onClick={() => changeViewMode('week')}
                            className={viewMode==='week' ? 'text-white' : ''}
                            style={viewMode==='week' ? { backgroundColor: branding?.primaryColor || '#3b82f6' } : {}}
                        >
                            Week
                        </Button>
                        <Button 
                            variant={viewMode==='day' ? 'default' : 'outline'} 
                            size="sm" 
                            onClick={() => changeViewMode('day')}
                            className={viewMode==='day' ? 'text-white' : ''}
                            style={viewMode==='day' ? { backgroundColor: branding?.primaryColor || '#3b82f6' } : {}}
                        >
                            Day
                        </Button>
                    </div>

                    <Button 
                        onClick={handleCreateEvent} 
                        className="ml-1 text-white"
                        style={{ backgroundColor: branding?.primaryColor || '#3b82f6' }}
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        New Event
                    </Button>
                </div>
            </div>

            {/* Calendar / Views */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                {viewMode === 'month' ? (
                    <div className="p-4">
                        {/* Weekday header */}
                        <div className="grid grid-cols-7 gap-3 mb-2 text-xs font-semibold text-muted-foreground">
                            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                                <div key={d} className="text-center">{d}</div>
                            ))}
                        </div>
                        {/* Month grid 6x7 starting Monday */}
                        <div className="grid grid-cols-7 gap-3">
                            {(() => {
                                const firstOfMonth = startOfMonth(currentDate);
                                const gridStart = startOfWeek(firstOfMonth, { weekStartsOn: 1 });
                                const cells = [];
                                for (let i = 0; i < 42; i++) {
                                    const day = addDays(gridStart, i);
                                    const isOtherMonth = day.getMonth() !== currentDate.getMonth();
                                    const dayStart = startOfDay(day);
                                    const dayEnd = endOfDay(day);
                                    const dayEvents = normEvents.filter(e => {
                                        const s = e.startAt;
                                        const en = e.endAt;
                                        return isWithinInterval(s, { start: dayStart, end: dayEnd }) ||
                                               isWithinInterval(en, { start: dayStart, end: dayEnd }) ||
                                               (s <= dayStart && en >= dayEnd);
                                    });
                                    // DEBUG: month cell events
                                    try {
                                        if (process.env.NODE_ENV !== 'production' && dayEvents.length > 0) {
                                            console.log('[Calendar][month] day:', format(day, 'yyyy-MM-dd'), 'events:', dayEvents.map(ev => ({ id: ev._id, title: ev.title, s: ev.startAt?.toISOString?.(), e: ev.endAt?.toISOString?.() })));
                                        }
                                    } catch (_) {}
                                    const isToday = isSameDay(day, new Date());
                                    const todayRingColor = branding?.primaryColor || '#3b82f6';
                                    cells.push(
                                        <div 
                                            key={day.toISOString()} 
                                            className={`border border-border rounded-lg p-2 min-h-[110px] ${isOtherMonth ? 'bg-muted' : 'bg-card'} ${isToday ? 'ring-2 ring-offset-1' : ''}`}
                                            style={isToday ? { '--tw-ring-color': todayRingColor, ringColor: todayRingColor } : {}}
                                        >
                                            <div className={`text-xs font-medium ${isOtherMonth ? 'text-muted-foreground/70' : 'text-foreground'}`}>{formatEU(day,'dd')}</div>
                                            <div className="mt-1 space-y-1">
                                                {dayEvents.slice(0,3).map(e => (
                                                    <div key={e._id} className="text-[11px] px-2 py-1 rounded cursor-pointer truncate flex items-center gap-1 justify-between" style={{ backgroundColor: (e.color || getEventTypeColor(e.type))+'20', color: '#111' }} onClick={() => openEventDetails(e)}>
                                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: e.color || getEventTypeColor(e.type) }} />
                                                        <span className="truncate flex-1 ml-1">{e.title}</span>
                                                        <Badge variant="secondary" className="ml-1 !text-[10px] !px-1 !py-0.5 capitalize">{e.type?.replace('_',' ') || 'event'}</Badge>
                                                    </div>
                                                ))}
                                                {dayEvents.length > 3 && (
                                                    <div className="text-[10px] text-muted-foreground">+{dayEvents.length-3} more</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                return cells;
                            })()}
                        </div>
                        {eventsInRange.length === 0 && (
                            <div className="text-center text-muted-foreground text-sm py-6">No events in this period</div>
                        )}
                    </div>
                ) : viewMode === 'week' ? (
                    <div className="p-4">
                        <div className="grid grid-cols-7 gap-3">
                            {getWeekDays(currentDate).map((day) => {
                                const isToday = isSameDay(day, new Date());
                                return (
                                <div 
                                    key={day.toISOString()} 
                                    className={`border border-border rounded-lg p-3 min-h-[140px] ${isToday ? 'ring-2 ring-offset-1' : ''}`}
                                    style={isToday ? { '--tw-ring-color': branding?.primaryColor || '#3b82f6', ringColor: branding?.primaryColor || '#3b82f6' } : {}}
                                >
                                    <div className="text-xs font-semibold text-foreground mb-2">{formatEU(day, 'EEE dd-MM')}</div>
                                    <div className="space-y-2">
                                        {eventsInRange
                                            .filter(e => {
                                                const s = e.startAt;
                                                const en = e.endAt;
                                                const dStart = startOfDay(day);
                                                const dEnd = endOfDay(day);
                                                return isWithinInterval(s, { start: dStart, end: dEnd }) ||
                                                       isWithinInterval(en, { start: dStart, end: dEnd }) ||
                                                       (s <= dStart && en >= dEnd);
                                            })
                                            .map(e => (
                                                <div key={e._id} className="text-xs p-2 rounded border cursor-pointer hover:bg-muted" style={{ borderColor: e.color || getEventTypeColor(e.type) }} onClick={() => openEventDetails(e)}>
                                                    <div className="font-medium text-foreground truncate flex items-center gap-1">
                                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: e.color || getEventTypeColor(e.type) }} />
                                                        {e.title}
                                                        <span className="ml-auto"><Badge variant="secondary" className="!text-[10px] !px-1 !py-0.5 capitalize">{e.type?.replace('_',' ') || 'event'}</Badge></span>
                                                    </div>
                                                    <div className="text-muted-foreground">{formatEU(e.startAt, 'HH:mm')} - {formatEU(e.endAt, 'HH:mm')}</div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )})}
                        </div>
                        {eventsInRange.length === 0 && (
                            <div className="text-center text-muted-foreground text-sm py-6">No events this week</div>
                        )}
                    </div>
                ) : (
                    <div className="p-4">
                        <div className="border border-border rounded-lg">
                            <div className="px-3 py-2 border-b text-sm font-semibold text-foreground">{formatEU(currentDate, 'EEEE dd-MM-yyyy')}</div>
                            <div className="p-3 space-y-2">
                                {eventsInRange
                                    .filter(e => {
                                        const s = e.startAt;
                                        const en = e.endAt;
                                        const dStart = startOfDay(currentDate);
                                        const dEnd = endOfDay(currentDate);
                                        return isWithinInterval(s, { start: dStart, end: dEnd }) ||
                                               isWithinInterval(en, { start: dStart, end: dEnd }) ||
                                               (s <= dStart && en >= dEnd);
                                    })
                                    .map(e => (
                                        <div key={e._id} className="text-sm p-3 rounded border cursor-pointer hover:bg-muted" style={{ borderColor: e.color || getEventTypeColor(e.type) }} onClick={() => openEventDetails(e)}>
                                            <div className="font-medium text-foreground flex items-center gap-2">
                                                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: e.color || getEventTypeColor(e.type) }} />
                                                {e.title}
                                                <span className="ml-auto"><Badge variant="secondary" className="!text-[10px] !px-1 !py-0.5 capitalize">{e.type?.replace('_',' ') || 'event'}</Badge></span>
                                            </div>
                                            <div className="text-muted-foreground text-xs">{formatEU(e.startAt, 'HH:mm')} - {formatEU(e.endAt, 'HH:mm')}</div>
                                            {e.location && <div className="text-muted-foreground text-xs">{e.location}</div>}
                                        </div>
                                    ))}
                                {eventsInRange.filter(e => {
                                    const s = e.startAt;
                                    const en = e.endAt;
                                    const dStart = startOfDay(currentDate);
                                    const dEnd = endOfDay(currentDate);
                                    return isWithinInterval(s, { start: dStart, end: dEnd }) ||
                                           isWithinInterval(en, { start: dStart, end: dEnd }) ||
                                           (s <= dStart && en >= dEnd);
                                }).length === 0 && (
                                    <div className="text-muted-foreground text-sm">No events</div>
                                )}
                            </div>
                        </div>
                            </div>
                        )}
            </div>

            {/* Upcoming Events */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Upcoming Events</h3>
                {events.filter(e => new Date(e.startAt) >= new Date()).slice(0, 5).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No upcoming events</p>
                ) : (
                    <div className="space-y-3">
                        {events
                            .filter(e => new Date(e.startAt) >= new Date())
                            .slice(0, 5)
                            .map(event => (
                                <div
                                    key={event._id}
                                    onClick={() => handleEditEvent(event)}
                                    className="flex items-start p-4 border border-border rounded-lg hover:bg-muted cursor-pointer"
                                >
                                    <div
                                        className="h-3 w-3 rounded-full mt-1 mr-3 flex-shrink-0"
                                        style={{ backgroundColor: event.color || getEventTypeColor(event.type) }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-foreground">{event.title}</p>
                                        <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                                            <span className="flex items-center">
                                                <ClockIcon className="h-4 w-4 mr-1" />
                                                {format(new Date(event.startAt), 'MMM d, h:mm a')}
                                            </span>
                                            {event.location && (
                                                <span className="flex items-center">
                                                    <MapPinIcon className="h-4 w-4 mr-1" />
                                                    {event.location}
                                                </span>
                                            )}
                                            {event.clientId && (
                                                <span className="flex items-center">
                                                    <UserGroupIcon className="h-4 w-4 mr-1" />
                                                    {event.clientId.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>

            {/* Event Modal (shadcn Dialog) */}
            <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{selectedEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Event Title *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Start Date & Time *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="dd-mm-yyyy HH:mm"
                                        value={formData.startAtEU}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const dt = parseEUInput(val);
                                            setFormData({
                                                ...formData,
                                                startAtEU: val,
                                                startAt: dt ? format(dt, "yyyy-MM-dd'T'HH:mm") : formData.startAt
                                            })
                                        }}
                                        required
                                        className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                    />
                                    {formErrors.startAtEU && (
                                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">{formErrors.startAtEU}</div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        End Date & Time *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="dd-mm-yyyy HH:mm"
                                        value={formData.endAtEU}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const dt = parseEUInput(val);
                                            setFormData({
                                                ...formData,
                                                endAtEU: val,
                                                endAt: dt ? format(dt, "yyyy-MM-dd'T'HH:mm") : formData.endAt
                                            })
                                        }}
                                        required
                                        className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                    />
                                    <div className="flex items-center justify-between mt-1">
                                        {formErrors.endAtEU ? (
                                            <div className="text-xs text-red-600 dark:text-red-400">{formErrors.endAtEU}</div>
                                        ) : <span />}
                                        <button 
                                            type="button" 
                                            className="text-xs hover:opacity-80 transition-opacity" 
                                            style={{ color: branding?.primaryColor || '#3b82f6' }}
                                            onClick={() => {
                                            const sdt = parseEUInput(formData.startAtEU) || new Date();
                                            const plus = new Date(sdt.getTime() + 60 * 60 * 1000);
                                            setFormData({
                                                ...formData,
                                                endAtEU: format(plus, 'dd-MM-yyyy HH:mm'),
                                                endAt: format(plus, "yyyy-MM-dd'T'HH:mm")
                                            });
                                        }}>+1 hour</button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Type
                                    </label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                    >
                                        <option value="meeting">Meeting</option>
                                        <option value="deadline">Deadline</option>
                                        <option value="appointment">Appointment</option>
                                        <option value="task_due">Task Due</option>
                                        <option value="reminder">Reminder</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Client
                                    </label>
                                    <select
                                        value={formData.clientId}
                                        onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                                        className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                    >
                                        <option value="">No client</option>
                                        {clients.map(client => (
                                            <option key={client._id} value={client._id}>
                                                {`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown Client'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Attendees
                                </label>
                                <select
                                    multiple
                                    value={formData.attendees}
                                    onChange={(e) => {
                                        const options = Array.from(e.target.selectedOptions).map(o => o.value);
                                        setFormData({ ...formData, attendees: options });
                                    }}
                                    className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring min-h-[90px]"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                >
                                    {agents.map(user => (
                                        <option key={user._id} value={user._id}>{user.name} ({user.email})</option>
                                    ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">Hold Ctrl/Cmd to select multiple</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="e.g., Office, Zoom"
                                    className="w-full px-4 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    style={{ '--tw-ring-color': branding?.primaryColor || '#3b82f6' }}
                                />
                            </div>

                            <DialogFooter className="pt-4 border-t border-border">
                                {selectedEvent && (
                                    <Button type="button" variant="outline" onClick={handleDeleteEvent} className="border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/10">
                                        Delete
                                    </Button>
                                )}
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setShowEventModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    className="text-white"
                                    style={{ 
                                        backgroundColor: branding?.primaryColor || '#3b82f6'
                                    }}
                                >
                                    {selectedEvent ? 'Update' : 'Create'} Event
                                </Button>
                            </DialogFooter>
                        </form>
                </DialogContent>
            </Dialog>

            {/* Event Details Dialog */}
            <Dialog open={showEventDetails} onOpenChange={setShowEventDetails}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: selectedEvent?.color || getEventTypeColor(selectedEvent?.type) }} />
                            {selectedEvent?.title || 'Event'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">When</span>
                            <span className="font-medium">{selectedEvent ? `${formatEU(new Date(selectedEvent.startAt), 'dd-MM-yyyy HH:mm')} → ${formatEU(new Date(selectedEvent.endAt), 'dd-MM-yyyy HH:mm')}` : ''}</span>
                        </div>
                        {selectedEvent?.type && (
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Type</span>
                                <Badge variant="secondary" className="capitalize">{selectedEvent.type.replace('_',' ')}</Badge>
                            </div>
                        )}
                        {selectedEvent?.location && (
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Location</span>
                                <span className="font-medium truncate max-w-[60%] text-right">{selectedEvent.location}</span>
                            </div>
                        )}
                        {selectedEvent?.attendees?.length > 0 && (
                            <div>
                                <div className="text-muted-foreground mb-1">Attendees</div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedEvent.attendees.map((a) => (
                                        <Badge key={a._id || a} variant="secondary">{a.name || a.email || 'User'}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {selectedEvent?.clientId?.name && (
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Client</span>
                                <span className="font-medium">{selectedEvent.clientId.name}</span>
                            </div>
                        )}
                        {selectedEvent?.description && (
                            <div>
                                <div className="text-muted-foreground mb-1">Description</div>
                                <div className="bg-muted border border-border rounded p-3">{selectedEvent.description}</div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="pt-4">
                        <Button 
                            variant="outline" 
                            onClick={() => { setShowEventDetails(false); handleEditEvent(selectedEvent); }}
                            style={{ borderColor: branding?.primaryColor || '#3b82f6', color: branding?.primaryColor || '#3b82f6' }}
                        >
                            Edit
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => { setShowEventDetails(false); handleDeleteEvent(); }} 
                            className="border-red-300 text-red-700 hover:bg-red-50"
                        >
                            Delete
                        </Button>
                        <Button 
                            onClick={() => setShowEventDetails(false)}
                            className="text-white"
                            style={{ 
                                backgroundColor: branding?.primaryColor || '#3b82f6'
                            }}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Calendar;





